import React, { Fragment, useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  useAPI,
  Button,
  Image,
  Loader,
  ViewContext,
  AuthContext,
} from "components/lib";
import Axios from "axios";
import { StylesManager, Model, QuestionTagboxModel } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
import { applyStackgoTheme } from "components/survey/stackgo-theme";

StylesManager.applyTheme("defaultV2");

// feature 008 (U6): the XPM custom-field DEFINITION type vocabulary, used only
// by the free-text fallback below — when the listing endpoint answers, the type
// comes from XPM itself and is never picked by hand.
//
// These are the values XPM READS BACK, which are NOT the values we POST when we
// create a field: integrationController creates <Type>Dropdown</Type> and
// <Type>Link</Type>, and XPM returns them as "Dropdown List" and "Value Link"
// (KTD2). Whatever is chosen here is stored VERBATIM and handed to the write
// path's element resolver as-is — nothing on this page translates a definition
// type into an XML element, and nothing checks it against a closed set.
const XPM_CUSTOM_FIELD_TYPES = [
  { value: "Text", text: "Text" },
  { value: "Dropdown List", text: "Dropdown list" },
  { value: "Value Link", text: "Link" },
  { value: "Checkbox", text: "Checkbox (yes/no)" },
  { value: "Date", text: "Date" },
  { value: "Number", text: "Number" },
  { value: "Decimal", text: "Decimal" },
];

// The prefixed source grammar, worded exactly as the profile_mappings table
// below words it (KTD3). The two tables sit next to each other and take the
// same vocabulary, so they are deliberately not paraphrased apart.
const SOURCE_VOCABULARY =
  "intake:<question>, verification:first_name|last_name|email, ra:<key>, or literal:<value>";

export function XpmSettingsView(props) {
  const exstingSettingsResponse = useAPI("/api/integration/xpm/settings");
  const xeroConnected = useAPI("/api/integration/xero/connected");
  const customFieldsResponse = useAPI("/api/integration/xpm/custom-fields");
  const [xeroConnections, setXeroConnections] = useState([]);
  const [mappableCustomFields, setMappableCustomFields] = useState([]);
  const [jobOnlyCustomFields, setJobOnlyCustomFields] = useState([]);

  const env = process.env.NODE_ENV;
  const navigateToUrl =
    env === "production"
      ? `https://identity.stackgo.io/account`
      : `http://localhost:3000/account`;

  useEffect(async () => {
    if (xeroConnected.loading === false && xeroConnected.data === true) {
      // get connections
      const getXeroConnections = async () => {
        const xeroConnections = await Axios.get(
          "/api/integration/xero/connections"
        );

        return xeroConnections;
      };

      const xeroConnections = await getXeroConnections();
      // map to create connections dropdown
      console.log("xero connections", xeroConnections);
      const connections = xeroConnections.data.data.map((connection) => {
        console.log(connection);
        return { value: connection.tenantId, text: connection.tenantName };
      });
      setXeroConnections(connections);
    }
  }, [xeroConnected]);

  // feature 008 (U6): split the tenant's custom field definitions into the ones
  // a created client can actually carry and the ones it cannot. Same shape as
  // the Zendesk Sell / HubSpot field pickers — useAPI, then filter by type into
  // state — so there is one remote-choices pattern in this directory, not four.
  useEffect(() => {
    if (customFieldsResponse.loading === false) {
      const definitions = Array.isArray(customFieldsResponse?.data)
        ? customFieldsResponse.data
        : [];

      // A definition with UseClient and UseContact both false is a JOB field
      // (KTD2). It cannot be written on a created client, so offering it would
      // produce a mapping that saves, looks configured, and is rejected by XPM
      // at write time. Hidden from the picker and named in the description
      // instead, so an admin looking for a field they can see in XPM is told
      // why it is missing rather than left guessing.
      setMappableCustomFields(
        definitions.filter((field) => field.useClient || field.useContact)
      );
      setJobOnlyCustomFields(
        definitions
          .filter((field) => !field.useClient && !field.useContact)
          .map((field) => field.name)
          .filter(Boolean)
      );
    }
  }, [customFieldsResponse]);

  function setExistingXpmSettings() {
    if (
      exstingSettingsResponse.loading === false &&
      exstingSettingsResponse.data !== null
    ) {
      const settings = exstingSettingsResponse?.data?.[0];
      console.log("settings", settings);
      console.log(xeroConnections);
      survey.setValue("enabled", settings?.enabled);
      survey.setValue("tenantId", settings?.data?.tenantId);
      survey.setValue("tranche2_enabled", settings?.data?.tranche2?.enabled === true);

      // feature 008: hydrate the create-client-on-completion block.
      const cc = settings?.data?.create_client_on_completion;
      survey.setValue("create_on_completion_enabled", cc?.enabled === true);
      survey.setValue(
        "create_on_completion_trigger",
        cc?.trigger || "risk_assessment_complete"
      );
      survey.setValue(
        "default_business_structure",
        cc?.default_business_structure || "Individual"
      );
      survey.setValue("entity_type_source", cc?.entity_type_source || "");
      // feature 008 (U9): the two TFN settings, both stored off by default. Read
      // with `=== true` rather than passed through, so a legacy record holding a
      // truthy string cannot render the toggle on when the server reads it off.
      survey.setValue("write_tfn_enabled", cc?.write_tfn?.enabled === true);
      survey.setValue(
        "redact_tfn_at_intake_enabled",
        cc?.redact_tfn_at_intake === true
      );
      survey.setValue(
        "profile_mappings",
        Object.entries(cc?.field_mappings?.profile || {}).map(
          ([xpm_field, source]) => ({ xpm_field, source })
        )
      );
      // feature 008 (U6): custom mappings are stored keyed by XPM field UUID as
      // { type, valueElement, source }. The matrix only ever edits the field and
      // the source — type and valueElement come from XPM (or, in the fallback,
      // from the row's own type column) and are re-attached on save. A bare
      // string is tolerated on the way in because the key was writable by API
      // before this page could edit it.
      survey.setValue(
        "custom_mappings",
        Object.entries(cc?.field_mappings?.custom || {}).map(
          ([xpm_field, entry]) => ({
            xpm_field,
            source:
              (typeof entry === "string" ? entry : entry?.source) || "",
            field_type:
              (entry && typeof entry === "object" && entry.type) || "",
          })
        )
      );
    }
  }

  // feature 008 (U6): everything the custom-field mapping table needs, derived
  // once per render because the SurveyJS model below is rebuilt on every render.
  const storedCustomMappings =
    exstingSettingsResponse?.data?.[0]?.data?.create_client_on_completion
      ?.field_mappings?.custom || {};

  const customFieldsById = mappableCustomFields.reduce((acc, field) => {
    acc[field.id] = field;
    return acc;
  }, {});

  // Whether the picker is usable at all. A listing that failed, a tenant that is
  // not connected yet, and a tenant whose only definitions are job-only all
  // arrive here as the same fact — no field is offerable — and all three need
  // the same escape hatch, so the fallback is driven off that rather than off an
  // error flag. useAPI has none to read: its catch hands the error to
  // ViewContext and leaves `loading` true, so "failed" is not a state this
  // component can observe directly.
  const useCustomFieldPicker = mappableCustomFields.length > 0;

  const customFieldChoices = useCustomFieldPicker
    ? [
        ...mappableCustomFields.map((field) => ({
          value: field.id,
          // The definition type is shown, not hidden, so an admin can see what
          // they are mapping into before the write path has to guess at it.
          text: field.type ? `${field.name} (${field.type})` : field.name,
        })),
        // A stored mapping whose field the listing did not return — deleted in
        // XPM, or switched to Jobs-only since it was mapped. Offered anyway,
        // and labelled: a dropdown with no matching choice renders an empty
        // cell over a mapping that is still being written, which the admin can
        // neither read nor knowingly remove. Nothing new can be mapped to one
        // of these — they are only ever choices because they are already saved.
        ...Object.keys(storedCustomMappings)
          .filter((uuid) => !customFieldsById[uuid])
          .map((uuid) => ({
            value: uuid,
            text: `${uuid} (unavailable — deleted or Jobs-only in XPM)`,
          })),
      ]
    : [];

  // The definition type for a mapped UUID, in order of authority: the live
  // listing, the row's own type column (fallback mode only), then what was
  // stored last time — the last covers a field deleted in XPM, and a listing
  // that failed on this page load but worked on the load that saved the mapping.
  // "" means the mapping is unwritable: the server needs a type to pick the XML
  // element, so a typeless row is refused at save (see the validator below)
  // rather than stored looking configured and silently dropped at write time.
  function resolveCustomFieldType(uuid, rowType) {
    const stored = storedCustomMappings[uuid];
    return (
      customFieldsById[uuid]?.type ||
      (typeof rowType === "string" ? rowType.trim() : "") ||
      (stored && typeof stored === "object" && stored.type) ||
      ""
    );
  }

  // XPM's own answer to "which XML element carries this field's value". Carried
  // through verbatim and never derived here; "" is a legitimate value (older
  // tenants omit it) and the write path falls back to the type table for it.
  function resolveCustomFieldValueElement(uuid) {
    const stored = storedCustomMappings[uuid];
    return (
      customFieldsById[uuid]?.valueElement ||
      (stored && typeof stored === "object" && stored.valueElement) ||
      ""
    );
  }

  const customFieldsDescription = [
    `Each row maps an XPM custom field to a source: ${SOURCE_VOCABULARY}. Leave empty to write no custom fields.`,
    useCustomFieldPicker
      ? null
      : "Your XPM custom fields could not be listed, so enter each field's UUID from XPM and pick the type XPM shows for it. The type is required: without it the mapping is stored but nothing is written.",
    useCustomFieldPicker && jobOnlyCustomFields.length
      ? `Not offered because they are enabled for Jobs only in XPM and a client cannot carry them: ${jobOnlyCustomFields.join(
          ", "
        )}.`
      : null,
  ]
    .filter(Boolean)
    .join(" ");

  // surveyjs json
  const json = {
    completedHtml: "<h3>Your changes have been saved.</h3>",
    showQuestionNumbers: "off",
    pages: [
      {
        name: "Manage your XPM integration",
        elements: [
          {
            type: "boolean",
            name: "enabled",
            title: "Enable XPM integration",
            isRequired: true,
          },
          {
            type: "dropdown",
            name: "tenantId",
            title: "Select a Xero connection",
            isRequired: true,
            choices: xeroConnections,
          },
          {
            type: "boolean",
            name: "tranche2_enabled",
            title: "Enable Tranche 2 (AML) triggers & writebacks",
            description: "When enabled on connect, Tranche 2 custom fields are created in Xero Practice Manager. Set the 'AML Command' custom field to trigger a Tranche 2 verification; results are written back to the client.",
            defaultValue: false,
          },
          {
            type: "boolean",
            name: "create_on_completion_enabled",
            title: "Create an XPM client when a public-intake verification completes",
            description:
              "Insert-only: a new XPM client is created for each public-form client at the point you choose below. (Email-match upsert is coming in a later release.)",
            defaultValue: false,
            visibleIf: "{enabled} = true",
          },
          {
            type: "dropdown",
            name: "create_on_completion_trigger",
            title: "When to create the XPM client",
            defaultValue: "risk_assessment_complete",
            visibleIf: "{enabled} = true and {create_on_completion_enabled} = true",
            description:
              "Earlier triggers create the client sooner but before later checks finish: 'after intake' creates even if the biometric later fails; 'after biometric' creates before the risk assessment.",
            choices: [
              { value: "risk_assessment_complete", text: "After the risk assessment is completed" },
              { value: "biometric_pass", text: "After the biometric check passes" },
              { value: "intake_complete", text: "After the intake form is submitted" },
            ],
          },
          {
            type: "dropdown",
            name: "default_business_structure",
            title: "Default client type",
            defaultValue: "Individual",
            visibleIf: "{enabled} = true and {create_on_completion_enabled} = true",
            choices: [
              { value: "Individual", text: "Individual" },
              { value: "Company", text: "Company / Organisation" },
            ],
          },
          {
            // feature 008 (U9): the per-account TFN writeback opt-in (R4/R5).
            // Worded so an admin cannot enable it without knowing what leaves
            // our system and where it lands — this is a tax file number going
            // to a third party, and "TFN sync" would not have said that.
            type: "boolean",
            name: "write_tfn_enabled",
            title: "Write the client's TFN to the XPM client's Tax Number field",
            description:
              "Off by default. When on, the tax file number your intake form collects is written to the Tax Number field on the XPM client created above, and is kept in the verification record until that write completes or fails — then removed, with a single-view recovery link sent to your notification address. Leave this off unless your firm needs the TFN in XPM.",
            defaultValue: false,
            visibleIf: "{enabled} = true and {create_on_completion_enabled} = true",
          },
          {
            // DELIBERATELY HIDDEN — not an oversight, and not dead config.
            // entity_type_source is honoured by the server (resolveEntityTypeKey
            // in model/xpmCreate.js) and any value already stored survives a save
            // of this page, but it is not offered in the UI: it is a free-text
            // intake question NAME with nothing to check it against — this page
            // has no list of the firm's intake questions — so a typo would save
            // cleanly, look configured, and silently route every client to the
            // default client type below. The visible "Default client type"
            // dropdown covers the case firms actually have (one client type per
            // public form). Expose this once there is a question picker to back
            // it; setting it via the API in the meantime works and sticks.
            type: "text",
            name: "entity_type_source",
            title: "Intake question that decides individual vs organisation (optional)",
            visible: false,
            description:
              "The intake form question name whose answer selects the client type. Leave blank to always use the default above.",
          },
          {
            type: "matrixdynamic",
            name: "profile_mappings",
            title: "Map intake questions to XPM profile fields",
            visible: false,
            // Reads from the shared constant so this table and the custom-field
            // table below cannot drift into describing the same grammar two
            // different ways. The rendered text is unchanged.
            description: `Each row maps an XPM static profile field to a source: ${SOURCE_VOCABULARY}. Leave empty to use sensible defaults.`,
            visibleIf: "{enabled} = true and {create_on_completion_enabled} = true",
            columns: [
              { name: "xpm_field", title: "XPM profile field", cellType: "text" },
              { name: "source", title: "Source", cellType: "text" },
            ],
            rowCount: 0,
            addRowText: "Add mapping",
          },
          {
            // feature 008 (U6): the sibling of profile_mappings above, for XPM
            // CUSTOM fields. Same prefixed source grammar (KTD3) — the only
            // difference is the left-hand column, which names a field by UUID
            // rather than by static profile alias.
            //
            // One question, two column sets. The picker's choices are a JS-side
            // fact (did the tenant's listing come back?), and a SurveyJS
            // `visibleIf` cannot read one — it evaluates over survey data, and
            // routing the fact through a hidden question would put it at the
            // mercy of the same clearInvisibleValues pass documented below. So
            // the two modes share a name, a hydration and a read on save, and
            // differ only in `columns`.
            type: "matrixdynamic",
            name: "custom_mappings",
            title: "Map intake questions to XPM custom fields",
            visible: false,
            description: customFieldsDescription,
            visibleIf: "{enabled} = true and {create_on_completion_enabled} = true",
            columns: useCustomFieldPicker
              ? [
                  {
                    name: "xpm_field",
                    title: "XPM custom field",
                    cellType: "dropdown",
                    choices: customFieldChoices,
                    isRequired: true,
                  },
                  { name: "source", title: "Source", cellType: "text", isRequired: true },
                ]
              : [
                  // The degradation route. A bare UUID is not enough on its own:
                  // the write path picks the XML element from the definition
                  // type, so a typeless row would save, reload looking
                  // configured, and write nothing — on the exact path that
                  // exists to keep the feature usable when the listing is down.
                  // Hence a required type column, not an optional one.
                  {
                    name: "xpm_field",
                    title: "XPM custom field UUID",
                    cellType: "text",
                    isRequired: true,
                  },
                  {
                    name: "field_type",
                    title: "Field type in XPM",
                    cellType: "dropdown",
                    choices: XPM_CUSTOM_FIELD_TYPES,
                    isRequired: true,
                  },
                  { name: "source", title: "Source", cellType: "text", isRequired: true },
                ],
            rowCount: 0,
            addRowText: "Add mapping",
          },
          {
            // feature 008 (U9 / KTD12): the OTHER half of the TFN story, and
            // deliberately not gated on create-on-completion — it is for the
            // firm that collects a TFN and does NOT send it to XPM, which is
            // every firm on the default. Gating it behind the creation panel
            // would hide it from exactly the accounts it is for.
            //
            // Default off, and the default is the point: turning this on
            // fleet-wide would change what every tenant's stored submissions
            // contain, so enablement is staged one account at a time.
            type: "boolean",
            name: "redact_tfn_at_intake_enabled",
            title: "Remove TFNs from public intake submissions when they arrive",
            description:
              "Off by default, which keeps the current behaviour: a tax file number submitted through your public intake form is stored with the rest of the answers. When on, it is replaced with REDACTED as the submission is received and a single-view recovery link is sent to your notification address. You do not need this while the TFN writeback above is on — that path removes the TFN once the write to XPM resolves.",
            defaultValue: false,
            visibleIf: "{enabled} = true",
          },
        ],
      },
    ],
  };

  json.navigateToUrl = navigateToUrl;

  async function saveSurveyResults(url, json) {
    console.log("saveSettings", url, json);
    const response = await Axios.post(url, json);
  }

  const xpmSettingsComplete = useCallback((sender) => {
    // feature 007: nest the flat T2 toggle into the tranche2 block.
    const data = { ...sender.data, tranche2: { enabled: sender.data.tranche2_enabled === true } };
    delete data.tranche2_enabled;

    // feature 008: build the create-client-on-completion block (insert-only).
    //
    // SurveyJS clears the answers of invisible questions before onComplete
    // (clearInvisibleValues defaults to "onComplete"), and every question in this
    // block is gated on {enabled} and {create_on_completion_enabled}. So an admin
    // who saves this page with XPM toggled off — or with the create-on-completion
    // panel collapsed — reaches here with the whole block already blank in
    // sender.data. Reading it unconditionally is what wiped their trigger,
    // default client type and mappings on a save they made for an unrelated
    // reason (R9). Fall back to what is STORED, and read the form only for the
    // questions that were actually visible. Same defect and same fix as
    // client/src/views/account/tranche2_settings.js, and the same trap already
    // documented in helper/xpmCustomFields.js.
    const storedCreate =
      exstingSettingsResponse?.data?.[0]?.data?.create_client_on_completion || {};
    const storedMappings = storedCreate.field_mappings || {};
    // The panel's own visibleIf, restated: read the form only when it held real
    // answers. `create_on_completion_enabled` is itself hidden while the
    // integration is off, so it needs the stored fallback too — otherwise
    // disabling XPM for an afternoon would quietly switch client creation off
    // for good.
    const integrationEnabled = sender.data.enabled === true;
    const createPanelVisible =
      integrationEnabled && sender.data.create_on_completion_enabled === true;

    const profile = (Array.isArray(sender.data.profile_mappings)
      ? sender.data.profile_mappings
      : []
    ).reduce((acc, row) => {
      if (row && row.xpm_field && row.source) acc[row.xpm_field] = row.source;
      return acc;
    }, {});

    // feature 008 (U6): { "<uuid>": { type, valueElement, source } } per KTD3.
    //
    // type and valueElement are carried through VERBATIM from what XPM returned
    // — this page does not translate them into an XML element and does not check
    // them against a closed set. XPM reads back definition types it will not
    // accept on create ("Dropdown List", "Value Link"), so an allowlist here
    // would silently discard exactly the fields a firm is most likely to map.
    const custom = (Array.isArray(sender.data.custom_mappings)
      ? sender.data.custom_mappings
      : []
    ).reduce((acc, row) => {
      const uuid =
        typeof row?.xpm_field === "string" ? row.xpm_field.trim() : "";
      const source = typeof row?.source === "string" ? row.source.trim() : "";
      if (!uuid || !source) return acc;

      // Belt and braces to the validator: a row that reaches here with no type
      // is unwritable, and storing it would be the "looks configured, writes
      // nothing" failure. The validator refuses the save first, so this is
      // unreachable in practice — it is here so the shape posted is always the
      // shape the server can use.
      const type = resolveCustomFieldType(uuid, row.field_type);
      if (!type) return acc;

      acc[uuid] = {
        type,
        valueElement: resolveCustomFieldValueElement(uuid),
        source,
      };
      return acc;
    }, {});

    data.create_client_on_completion = {
      enabled: integrationEnabled
        ? sender.data.create_on_completion_enabled === true
        : storedCreate.enabled === true,
      mode: "insert",
      trigger: createPanelVisible
        ? sender.data.create_on_completion_trigger || "risk_assessment_complete"
        : storedCreate.trigger || "risk_assessment_complete",
      default_business_structure: createPanelVisible
        ? sender.data.default_business_structure || "Individual"
        : storedCreate.default_business_structure || "Individual",
      // feature 008 (U9): the toggle only. `source` is NOT sent — this page has
      // no intake-question picker to choose one with (the same argument that
      // keeps entity_type_source hidden), and mergeCreateOnCompletion merges
      // write_tfn per key, so an API-set source survives every save of this
      // page. Sending the block whole, with a source this page invented, is
      // what would wipe it.
      write_tfn: {
        enabled: createPanelVisible
          ? sender.data.write_tfn_enabled === true
          : storedCreate.write_tfn?.enabled === true,
      },
      // Gated on {enabled} alone, so it falls back on the same condition. An
      // admin who disables XPM for an afternoon must not silently lose intake
      // redaction along with it.
      redact_tfn_at_intake: integrationEnabled
        ? sender.data.redact_tfn_at_intake_enabled === true
        : storedCreate.redact_tfn_at_intake === true,
      // entity_type_source is deliberately ABSENT rather than sent empty: this
      // page does not edit it (see the hidden entity_type_source question
      // above), and mergeCreateOnCompletion carries over any key the payload
      // does not send. Sending it empty is what wiped it.
      field_mappings: {
        // An empty table IS meaningful while the panel is open — that is the
        // admin deleting their last mapping row — so only the collapsed case
        // falls back to the stored map.
        profile: createPanelVisible ? profile : storedMappings.profile || {},
        // `custom` used to be in the absent-by-design list above, alongside
        // entity_type_source, because nothing edited it. U6 does, so it has to
        // be sent: left absent it would carry over forever and an admin could
        // never delete their last custom mapping. It gets exactly the profile
        // treatment — posted as edited while the panel is open, kept as stored
        // while it is collapsed, because SurveyJS has already blanked the table
        // by the time this runs in the collapsed case.
        custom: createPanelVisible ? custom : storedMappings.custom || {},
      },
    };
    delete data.create_on_completion_enabled;
    delete data.create_on_completion_trigger;
    delete data.default_business_structure;
    delete data.write_tfn_enabled;
    delete data.redact_tfn_at_intake_enabled;
    delete data.entity_type_source;
    delete data.profile_mappings;
    delete data.custom_mappings;

    saveSurveyResults("/api/integration/xpm/settings", data);
  });

  // feature 008 (U6): refuse a custom mapping whose definition type cannot be
  // resolved, instead of posting one the server's normalizer drops on the floor.
  // A mapping that saves cleanly, reloads looking configured, and writes nothing
  // to XPM is the failure this whole column exists to prevent — the admin has no
  // way to tell it apart from a working one until a client is created without
  // the field.
  //
  // In fallback mode the type column is isRequired, so SurveyJS catches an empty
  // one first; this fires for the case that column cannot see — a row whose UUID
  // is no longer in the listing and was stored without a type. Invisible
  // questions are not validated, so the collapsed panel is unaffected.
  const validateCustomMappings = useCallback((sender, options) => {
    if (options.question?.name !== "custom_mappings") return;

    const typeless = (Array.isArray(options.value) ? options.value : []).filter(
      (row) => {
        const uuid =
          typeof row?.xpm_field === "string" ? row.xpm_field.trim() : "";
        // An empty field cell is the column's own isRequired to report; saying
        // it twice in two different places just muddies the message.
        if (!uuid) return false;
        return !resolveCustomFieldType(uuid, row?.field_type);
      }
    );

    if (typeless.length) {
      options.error =
        "One or more mappings have no XPM field type, so nothing would be written. Remove the row, or re-select the field once XPM custom fields load.";
    }
  });

  // if hubspot is not connected - return to integrations page
  // if (xeroConnected.loading === false && xeroConnected.data === false) {
  //   window.location = "/account/integrations";
  // }

  const survey = new Model(json);
  applyStackgoTheme(survey);
  survey.onComplete.add(xpmSettingsComplete);
  survey.onValidateQuestion.add(validateCustomMappings);

  setExistingXpmSettings();

  return (
    <Fragment>
      <Survey model={survey} />
    </Fragment>
  );
}
